import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
} from "react";
import Chart from "chart.js/auto";
import "./PaymentAnalytics.css";

/* =========================================================================
   CONFIGURATION
   ========================================================================= */

const API_URL = `${process.env.REACT_APP_BACKEND_URL}course-payment-analytics`;

/*
 * MULTIPLE COURSE IDS
 *
 * Add all course IDs that should appear in the course selector.
 *
 * You can also configure them through:
 *
 * REACT_APP_COURSE_IDS=id1,id2,id3
 *
 * The UI sends all configured course IDs to the existing API so the selector can display course names:
 *
 * /course-payment-analytics?course_id=<selected-course-id>
 */
const COURSE_IDS = (
  process.env.REACT_APP_COURSE_IDS ||
  [
    "66487408f07c5f0f567ee3c3",
    "6a51d43add03c0ca536c473a",
    "6a97b4d244c35b22f9d47f15"
    
  ].join(",")
)
  .split(",")
  .map((id) => id.trim())
  .filter(Boolean);


const ICON_BASE_URL = (id) =>
  `https://api.maya.adityauniversity.in/node/technology-icons/${id}`;


/* =========================================================================
   ACCENTS
   ========================================================================= */

const ACCENTS = [
  "#0ea975",
  "#3b6df6",
  "#e08e1f",
  "#7c5cfa",
  "#e34d69",
  "#14b8a6",
];


/* =========================================================================
   HELPERS
   ========================================================================= */

function initials(name = "") {
  return String(name)
    .replace(/_\d{4}$/, "")
    .split(/[\s&]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
}


function cleanTechnologyName(name = "") {
  return String(name)
    .replace(/_\d{4}$/, "")
    .replace(/_B\d+$/, "")
    .trim();
}


function number(value) {
  const n = Number(value);

  return Number.isFinite(n) ? n : 0;
}


function percentage(value) {
  return `${number(value).toFixed(
    Number(value) % 1 === 0 ? 0 : 2
  )}%`;
}


/* =========================================================================
   ANIMATED NUMBER
   ========================================================================= */

function AnimatedNumber({
  value,
  percent = false,
  duration = 950,
}) {
  const [display, setDisplay] = useState(0);
  const raf = useRef(null);

  useEffect(() => {
    const target = number(value);
    const t0 = performance.now();

    function tick(now) {
      const p = Math.min(
        1,
        (now - t0) / duration
      );

      const eased =
        1 - Math.pow(1 - p, 3);

      setDisplay(
        Math.round(target * eased)
      );

      if (p < 1) {
        raf.current =
          requestAnimationFrame(tick);
      }
    }

    raf.current =
      requestAnimationFrame(tick);

    return () =>
      cancelAnimationFrame(
        raf.current
      );
  }, [value, duration]);

  return (
    <>
      {percent
        ? `${display}%`
        : display.toLocaleString()}
    </>
  );
}


/* =========================================================================
   KPI STRIP
   ========================================================================= */

function KPIStrip({
  course,
  selectedTerm,
}) {
  const technologies =
    course?.technologies || [];

  const registered =
    number(
      course?.overall
        ?.registered_students
    ) ||
    number(
      course?.total_course_registered
    );

  const totalSeats =
    technologies.reduce(
      (sum, tech) =>
        sum + number(tech?.seats),
      0
    );

  const termData =
    course?.overall_term_summary?.find(
      (term) =>
        String(term?.term_name) ===
        String(selectedTerm)
    );

  const paid =
    number(termData?.paid_students);

  const pending =
    number(termData?.pending_students);

  const paymentPercentage =
    number(
      termData?.payment_percentage
    );

  const openSeats =
    Math.max(
      0,
      totalSeats - registered
    );

  const registrationPercentage =
    totalSeats
      ? Math.round(
          (registered / totalSeats) *
            100
        )
      : 0;


  const kpiDefs = [
    {
      lbl: "Technologies",
      val: technologies.length,
      glow: "rgba(59,109,246,.16)",
      foot: `${technologies.length} active technologies`,
    },

    {
      lbl: "Total Seats",
      val: totalSeats,
      glow: "rgba(14,169,117,.16)",
      foot: "across all technologies",
    },

    {
      lbl: "Registered",
      val: registered,
      glow: "rgba(224,142,31,.16)",
      foot: `${registrationPercentage}% of total capacity`,
    },

    {
      lbl: `${selectedTerm} Paid`,
      val: paid,
      glow: "rgba(124,92,250,.16)",
      foot: `${pending} students pending`,
    },

    {
      lbl: "Payment %",
      val: paymentPercentage,
      glow: "rgba(227,77,105,.16)",
      foot: `${selectedTerm} collection status`,
      percent: true,
    },
  ];


  return (
    <div className="kpis">

      {kpiDefs.map(
        (k, i) => (
          <div
            className="kpi"
            key={k.lbl}
            style={{
              "--kpi-glow":
                k.glow,
              animationDelay:
                `${i * 0.07}s`,
            }}
          >

            <div className="lbl">
              {k.lbl}
            </div>

            <div className="num">
              <AnimatedNumber
                value={k.val}
                percent={
                  !!k.percent
                }
              />
            </div>

            <div className="foot">
              {k.foot}
            </div>

          </div>
        )
      )}

    </div>
  );
}


/* =========================================================================
   COURSE SELECTOR
   ========================================================================= */

function CourseSelector({
  courseIds,
  courseOptions,
  selectedCourseId,
  course,
  onChange,
  disabled = false,
}) {
  if (!courseIds.length) {
    return null;
  }

  return (
    <div className="term-selector course-selector">

      <div className="term-selector-label">

        <span className="term-selector-icon">
          C
        </span>

        <div>
          <div className="term-selector-title">
            Course
          </div>

          <div className="term-selector-sub">
            Select a course to view its payment analytics
          </div>
        </div>

      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          flexWrap: "wrap",
          flex: 1,
          justifyContent: "flex-end",
        }}
      >
        <select
          value={selectedCourseId}
          onChange={(event) => {
            // Reset the term immediately when switching courses.
            // The newly selected course will default to its first available term after loading.
            onChange(event.target.value);
          }}
          disabled={disabled}
          style={{
            minWidth: 280,
            maxWidth: "100%",
            padding: "10px 38px 10px 13px",
            borderRadius: 10,
            border: "1px solid var(--border-soft)",
            background: "var(--surface, #fff)",
            color: "var(--ink, #111827)",
            fontWeight: 700,
            fontSize: 12,
            outline: "none",
            cursor: disabled ? "not-allowed" : "pointer",
          }}
        >
          {courseIds.map((id) => {
            const option =
              courseOptions?.find(
                (item) => String(item?.course_id) === String(id)
              );

            const courseName =
              option?.course_name ||
              (id === selectedCourseId ? course?.course_name : null);

            return (
              <option key={id} value={id}>
                {courseName || `Course (${id})`}
              </option>
            );
          })}
        </select>
      </div>

    </div>
  );
}


/* =========================================================================
   TERM SELECTOR
   ========================================================================= */

function TermSelector({
  terms,
  selectedTerm,
  onChange,
}) {
  if (!terms.length) {
    return null;
  }

  return (
    <div className="term-selector">

      <div className="term-selector-label">
        <span className="term-selector-icon">
          T
        </span>

        <div>
          <div className="term-selector-title">
            Payment Term
          </div>

          <div className="term-selector-sub">
            View course analytics term-wise
          </div>
        </div>
      </div>


      <div className="term-buttons">

        {terms.map(
          (term) => (
            <button
              key={term}
              type="button"
              className={
                selectedTerm === term
                  ? "term-button active"
                  : "term-button"
              }
              onClick={() =>
                onChange(term)
              }
            >
              {term}
            </button>
          )
        )}

      </div>

    </div>
  );
}


/* =========================================================================
   CHARTS
   ========================================================================= */

function ChartsRow({
  course,
  selectedTerm,
}) {
  const barCanvasRef =
    useRef(null);

  const donutCanvasRef =
    useRef(null);

  const barChartRef =
    useRef(null);

  const donutChartRef =
    useRef(null);


  const technologies =
    course?.technologies || [];


  const termSummary =
    course?.overall_term_summary ||
    [];


  const selectedTermData =
    termSummary.find(
      (term) =>
        String(term?.term_name) ===
        String(selectedTerm)
    );


  const totalRegistered =
    number(
      course?.overall
        ?.registered_students
    ) ||
    number(
      course?.total_course_registered
    );


  const totalPaid =
    number(
      selectedTermData?.paid_students
    );


  const totalPending =
    number(
      selectedTermData?.pending_students
    );


  const totalSeats =
    technologies.reduce(
      (sum, tech) =>
        sum + number(tech?.seats),
      0
    );


  const openSeats =
    Math.max(
      0,
      totalSeats - totalRegistered
    );


  useEffect(() => {

    if (!technologies.length) {
      return;
    }


    const labels =
      technologies.map(
        (tech) =>
          cleanTechnologyName(
            tech?.technology_name
          )
      );


    const registeredData =
      technologies.map(
        (tech) =>
          number(
            tech?.registered_students
          )
      );


    const paidData =
      technologies.map(
        (tech) => {

          const term =
            tech?.terms?.find(
              (item) =>
                String(
                  item?.term_name
                ) ===
                String(
                  selectedTerm
                )
            );

          return number(
            term?.paid_students
          );
        }
      );


    const seatData =
      technologies.map(
        (tech) =>
          number(
            tech?.seats
          )
      );


    const colors =
      technologies.map(
        (_, i) =>
          ACCENTS[
            i % ACCENTS.length
          ]
      );


    /* -------------------------------------------------------------
       BAR CHART
       ------------------------------------------------------------- */

    if (
      barChartRef.current
    ) {
      barChartRef.current.destroy();
    }


    barChartRef.current =
      new Chart(
        barCanvasRef.current,
        {
          type: "bar",

          data: {
            labels,

            datasets: [
              {
                label:
                  "Seats",

                data:
                  seatData,

                backgroundColor:
                  "rgba(17,24,39,.06)",

                borderRadius: 6,

                barThickness: 13,
              },

              {
                label:
                  "Registered",

                data:
                  registeredData,

                backgroundColor:
                  "rgba(59,109,246,.35)",

                borderRadius: 6,

                barThickness: 13,
              },

              {
                label:
                  `${selectedTerm} Paid`,

                data:
                  paidData,

                backgroundColor:
                  colors,

                borderRadius: 6,

                barThickness: 13,
              },
            ],
          },

          options: {
            responsive: true,

            maintainAspectRatio:
              false,

            animation: {
              duration: 950,
              easing:
                "easeOutCubic",
            },

            plugins: {
              legend: {
                labels: {
                  color:
                    "#5b6472",

                  font: {
                    family:
                      "Inter",
                    size: 11,
                  },

                  boxWidth: 10,
                  boxHeight: 10,
                },
              },
            },

            scales: {
              x: {
                ticks: {
                  color:
                    "#5b6472",

                  font: {
                    size: 10,
                  },

                  maxRotation: 0,

                  autoSkip: true,
                },

                grid: {
                  display: false,
                },
              },

              y: {
                ticks: {
                  color:
                    "#5b6472",

                  font: {
                    size: 10,
                  },
                },

                grid: {
                  color:
                    "rgba(17,24,39,.06)",
                },

                beginAtZero:
                  true,
              },
            },
          },
        }
      );


    /* -------------------------------------------------------------
       DONUT
       ------------------------------------------------------------- */

    if (
      donutChartRef.current
    ) {
      donutChartRef.current.destroy();
    }


    donutChartRef.current =
      new Chart(
        donutCanvasRef.current,
        {
          type: "doughnut",

          data: {
            labels: [
              "Paid",
              "Registered, pending",
              "Open seats",
            ],

            datasets: [
              {
                data: [
                  totalPaid,
                  totalPending,
                  openSeats,
                ],

                backgroundColor: [
                  "#0ea975",
                  "#ffce94",
                  "rgba(17,24,39,.06)",
                ],

                borderWidth: 0,

                hoverOffset: 4,
              },
            ],
          },

          options: {
            responsive: true,

            maintainAspectRatio:
              false,

            cutout: "72%",

            animation: {
              duration: 1050,
              easing:
                "easeOutCubic",
            },

            plugins: {
              legend: {
                display: false,
              },
            },
          },
        }
      );


    return () => {

      if (
        barChartRef.current
      ) {
        barChartRef.current.destroy();
      }

      if (
        donutChartRef.current
      ) {
        donutChartRef.current.destroy();
      }

    };

  }, [
    technologies,
    selectedTerm,
    totalPaid,
    totalPending,
    openSeats,
  ]);


  if (!technologies.length) {
    return null;
  }


  return (
    <div className="charts-row">

      {/* ---------------------------------------------------------
          TECHNOLOGY BAR CHART
          --------------------------------------------------------- */}

      <div
        className="panel"
        style={{
          animationDelay:
            ".05s",
        }}
      >

        <h2>
          Technology-wise Enrollment
        </h2>

        <div className="panel-sub">
          Seats, registrations and{" "}
          {selectedTerm} payments
        </div>

        <div className="chart-box">
          <canvas
            ref={
              barCanvasRef
            }
          />
        </div>

      </div>


      {/* ---------------------------------------------------------
          FUNNEL
          --------------------------------------------------------- */}

      <div
        className="panel"
        style={{
          animationDelay:
            ".1s",
        }}
      >

        <h2>
          {selectedTerm} Payment Funnel
        </h2>

        <div className="panel-sub">
          Current registered and payment status
        </div>


        <div className="donut-wrap">

          <div
            className="chart-box"
            style={{
              height: 150,
              flex: 1,
            }}
          >

            <canvas
              ref={
                donutCanvasRef
              }
            />

          </div>


          <div className="donut-legend">

            <div className="legend-item">

              <span
                className="legend-dot"
                style={{
                  background:
                    "#0ea975",
                }}
              />

              {selectedTerm} Paid

              <b>
                {totalPaid}
              </b>

            </div>


            <div className="legend-item">

              <span
                className="legend-dot"
                style={{
                  background:
                    "#ffce94",
                }}
              />

              Pending

              <b>
                {totalPending}
              </b>

            </div>


            <div className="legend-item">

              <span
                className="legend-dot"
                style={{
                  background:
                    "rgba(17,24,39,.15)",
                }}
              />

              Open seats

              <b>
                {openSeats}
              </b>

            </div>


            <div
              className="legend-item"
              style={{
                marginTop: 5,
                borderTop:
                  "1px solid var(--border-soft)",
                paddingTop: 11,
              }}
            >

              Total capacity

              <b>
                {totalSeats}
              </b>

            </div>

          </div>

        </div>

      </div>

    </div>
  );
}


/* =========================================================================
   TECHNOLOGY CARD
   ========================================================================= */

function TechCard({
  tech,
  idx,
  selectedTerm,
}) {
  const [
    imgFailed,
    setImgFailed,
  ] = useState(false);


  const [
    paidPct,
    setPaidPct,
  ] = useState(0);


  const [
    registeredPct,
    setRegisteredPct,
  ] = useState(0);


  const accent =
    ACCENTS[
      idx % ACCENTS.length
    ];


  const seats =
    number(tech?.seats);


  const registered =
    number(
      tech?.registered_students
    );


  const term =
    tech?.terms?.find(
      (item) =>
        String(
          item?.term_name
        ) ===
        String(selectedTerm)
    );


  const paid =
    number(
      term?.paid_students
    );


  const pending =
    number(
      term?.pending_students
    );


  const paymentPercentage =
    number(
      term?.payment_percentage
    );


  const targetPaidPct =
    seats
      ? Math.min(
          100,
          Math.round(
            (paid / seats) * 100
          )
        )
      : 0;


  const targetRegisteredPct =
    seats
      ? Math.min(
          100,
          Math.round(
            (registered / seats) *
              100
          )
        )
      : 0;


  const iconUrl =
    tech?.technology_icon
      ? /^https?:\/\//i.test(
          tech.technology_icon
        )
        ? tech.technology_icon
        : ICON_BASE_URL(
            tech.technology_icon
          )
      : null;


  const displayName =
    cleanTechnologyName(
      tech?.technology_name
    );


  const cohortYear =
    (
      String(
        tech?.technology_name || ""
      ).match(
        /_(\d{4})/
      ) || []
    )[1];


  useEffect(() => {

    const raf =
      requestAnimationFrame(
        () => {

          setPaidPct(
            targetPaidPct
          );

          setRegisteredPct(
            targetRegisteredPct
          );

        }
      );


    return () =>
      cancelAnimationFrame(
        raf
      );

  }, [
    targetPaidPct,
    targetRegisteredPct,
  ]);


  return (
    <div
      className="card"
      style={{
        "--card-accent":
          accent,

        animationDelay:
          `${idx * 0.08}s`,
      }}
    >

      {/* ---------------------------------------------------------
          HEADER
          --------------------------------------------------------- */}

      <div className="card-head">

        <div className="icon-box">

          {iconUrl &&
          !imgFailed ? (

            <img
              src={iconUrl}
              alt={`${displayName} icon`}
              onError={() =>
                setImgFailed(true)
              }
            />

          ) : (

            <span className="fallback">
              {initials(
                tech?.technology_name
              )}
            </span>

          )}

        </div>


        <div className="name-block">

          <h3
            title={displayName}
          >
            {displayName}
          </h3>


          <div className="tid">

            {cohortYear
              ? `Cohort ${cohortYear} · `
              : ""}

            {String(
              tech?.technology_id ||
              ""
            ).slice(0, 10)}
            …

          </div>

        </div>

      </div>


      {/* ---------------------------------------------------------
          REGISTERED / SEATS
          --------------------------------------------------------- */}

      <div className="seat-row">

        <span>
          Registered / Seats
        </span>

        <b>
          {registered} / {seats}
        </b>

      </div>


      {/* ---------------------------------------------------------
          CAPACITY BAR
          --------------------------------------------------------- */}

      <div className="bar-track">

        <div
          className="bar-fill registered"
          style={{
            width:
              `${registeredPct}%`,
          }}
        />

        <div
          className="bar-fill"
          style={{
            width:
              `${paidPct}%`,
          }}
        />

      </div>


      {/* ---------------------------------------------------------
          SELECTED TERM PAID
          --------------------------------------------------------- */}

      <div
        className="seat-row"
        style={{
          marginTop: -12,
        }}
      >

        <span>
          {selectedTerm} Paid
        </span>

        <b>

          {paid}

          <span className="of-tag">
            {" "}
            of {registered} registered
          </span>

        </b>

      </div>


      {/* ---------------------------------------------------------
          PAYMENT STATUS
          --------------------------------------------------------- */}

      <div
        style={{
          display: "flex",
          justifyContent:
            "space-between",
          alignItems: "center",
          marginTop: 10,
          fontSize: 12,
        }}
      >

        <span
          style={{
            color:
              "var(--ink-dim)",
          }}
        >
          Pending
        </span>

        <strong>
          {pending}
        </strong>

      </div>


      {/* ---------------------------------------------------------
          PAYMENT PROGRESS
          --------------------------------------------------------- */}

      <div
        style={{
          display: "flex",
          justifyContent:
            "space-between",
          alignItems: "center",
          marginTop: 8,
          fontSize: 12,
        }}
      >

        <span
          style={{
            color:
              "var(--ink-dim)",
          }}
        >
          Payment %
        </span>

        <strong
          style={{
            color:
              paymentPercentage >=
              90
                ? "#0ea975"
                : paymentPercentage >=
                  50
                ? "#e08e1f"
                : "#e34d69",
          }}
        >
          {percentage(
            paymentPercentage
          )}
        </strong>

      </div>


      {/* ---------------------------------------------------------
          TERM SUMMARY
          --------------------------------------------------------- */}

      <div
        className="terms"
        style={{
          gridTemplateColumns:
            `repeat(${
              Math.min(
                tech?.terms?.length ||
                  1,
                4
              )
            },1fr)`,
        }}
      >

        {(
          tech?.terms || []
        ).map(
          (term) => {

            const count =
              number(
                term?.paid_students
              );


            const isSelected =
              String(
                term?.term_name
              ) ===
              String(
                selectedTerm
              );


            return (
              <div
                className={`term ${
                  count > 0
                    ? "paid"
                    : ""
                } ${
                  isSelected
                    ? "selected"
                    : ""
                }`}
                key={
                  term?.term_name
                }
              >

                <div className="t-count">
                  {count}
                </div>

                <div className="t-lbl">
                  {String(
                    term?.term_name ||
                      ""
                  ).replace(
                    /^Term\s*/i,
                    "T"
                  )}
                </div>

              </div>
            );

          }
        )}

      </div>

    </div>
  );
}


/* =========================================================================
   TERM-WISE COURSE SUMMARY TABLE
   ========================================================================= */

function TermSummaryTable({
  course,
  selectedTerm,
}) {
  const terms =
    course?.overall_term_summary ||
    [];


  const technologies =
    course?.technologies || [];


  if (!terms.length) {
    return null;
  }


  const selectedTermData =
    terms.find(
      (term) =>
        String(term?.term_name) ===
        String(selectedTerm)
    );


  return (
    <div className="panel">

      <div
        style={{
          display: "flex",
          justifyContent:
            "space-between",
          alignItems: "center",
          gap: 15,
          marginBottom: 18,
          flexWrap: "wrap",
        }}
      >

        <div>

          <h2>
            Course Term-wise Analytics
          </h2>

          <div className="panel-sub">
            Overall payment performance across all technologies
          </div>

        </div>


        <div
          style={{
            fontSize: 12,
            fontWeight: 700,
            padding:
              "7px 12px",
            borderRadius: 999,
            background:
              "rgba(59,109,246,.08)",
            color:
              "#3b6df6",
          }}
        >
          {selectedTerm}
        </div>

      </div>


      <div
        style={{
          overflowX:
            "auto",
        }}
      >

        <table
          style={{
            width: "100%",
            borderCollapse:
              "collapse",
            minWidth: 700,
          }}
        >

          <thead>

            <tr>

              <th
                style={{
                  textAlign: "left",
                  padding: 12,
                  fontSize: 11,
                  color:
                    "#5b6472",
                  borderBottom:
                    "1px solid var(--border-soft)",
                }}
              >
                Term
              </th>

              <th
                style={{
                  textAlign: "right",
                  padding: 12,
                  fontSize: 11,
                  color:
                    "#5b6472",
                  borderBottom:
                    "1px solid var(--border-soft)",
                }}
              >
                Registered
              </th>

              <th
                style={{
                  textAlign: "right",
                  padding: 12,
                  fontSize: 11,
                  color:
                    "#5b6472",
                  borderBottom:
                    "1px solid var(--border-soft)",
                }}
              >
                Paid
              </th>

              <th
                style={{
                  textAlign: "right",
                  padding: 12,
                  fontSize: 11,
                  color:
                    "#5b6472",
                  borderBottom:
                    "1px solid var(--border-soft)",
                }}
              >
                Pending
              </th>

              <th
                style={{
                  textAlign: "right",
                  padding: 12,
                  fontSize: 11,
                  color:
                    "#5b6472",
                  borderBottom:
                    "1px solid var(--border-soft)",
                }}
              >
                Payment %
              </th>

            </tr>

          </thead>


          <tbody>

            {terms.map(
              (term) => {

                const isSelected =
                  String(
                    term?.term_name
                  ) ===
                  String(
                    selectedTerm
                  );


                return (
                  <tr
                    key={
                      term?.term_name
                    }
                    style={{
                      background:
                        isSelected
                          ? "rgba(59,109,246,.045)"
                          : "transparent",
                    }}
                  >

                    <td
                      style={{
                        padding: 12,
                        fontWeight:
                          isSelected
                            ? 700
                            : 500,
                        borderBottom:
                          "1px solid var(--border-soft)",
                      }}
                    >
                      {term?.term_name}
                    </td>


                    <td
                      style={{
                        padding: 12,
                        textAlign:
                          "right",
                        borderBottom:
                          "1px solid var(--border-soft)",
                      }}
                    >
                      {number(
                        term?.registered_students
                      ).toLocaleString()}
                    </td>


                    <td
                      style={{
                        padding: 12,
                        textAlign:
                          "right",
                        fontWeight: 700,
                        color:
                          "#0ea975",
                        borderBottom:
                          "1px solid var(--border-soft)",
                      }}
                    >
                      {number(
                        term?.paid_students
                      ).toLocaleString()}
                    </td>


                    <td
                      style={{
                        padding: 12,
                        textAlign:
                          "right",
                        fontWeight: 600,
                        borderBottom:
                          "1px solid var(--border-soft)",
                      }}
                    >
                      {number(
                        term?.pending_students
                      ).toLocaleString()}
                    </td>


                    <td
                      style={{
                        padding: 12,
                        textAlign:
                          "right",
                        fontWeight: 700,
                        color:
                          isSelected
                            ? "#3b6df6"
                            : "inherit",
                        borderBottom:
                          "1px solid var(--border-soft)",
                      }}
                    >
                      {percentage(
                        term?.payment_percentage
                      )}
                    </td>

                  </tr>
                );

              }
            )}

          </tbody>

        </table>

      </div>


      <div
        style={{
          marginTop: 16,
          display: "grid",
          gridTemplateColumns:
            "repeat(auto-fit,minmax(180px,1fr))",
          gap: 10,
        }}
      >

        <div
          style={{
            padding: 13,
            borderRadius: 10,
            background:
              "rgba(14,169,117,.06)",
          }}
        >

          <div
            style={{
              fontSize: 10,
              color:
                "var(--ink-dim)",
              textTransform:
                "uppercase",
              fontWeight: 700,
              letterSpacing:
                ".05em",
            }}
          >
            Selected Term Paid
          </div>

          <div
            style={{
              marginTop: 4,
              fontSize: 22,
              fontWeight: 800,
            }}
          >
            {number(
              selectedTermData?.paid_students
            )}
          </div>

        </div>


        <div
          style={{
            padding: 13,
            borderRadius: 10,
            background:
              "rgba(224,142,31,.06)",
          }}
        >

          <div
            style={{
              fontSize: 10,
              color:
                "var(--ink-dim)",
              textTransform:
                "uppercase",
              fontWeight: 700,
              letterSpacing:
                ".05em",
            }}
          >
            Selected Term Pending
          </div>

          <div
            style={{
              marginTop: 4,
              fontSize: 22,
              fontWeight: 800,
            }}
          >
            {number(
              selectedTermData?.pending_students
            )}
          </div>

        </div>


        <div
          style={{
            padding: 13,
            borderRadius: 10,
            background:
              "rgba(59,109,246,.06)",
          }}
        >

          <div
            style={{
              fontSize: 10,
              color:
                "var(--ink-dim)",
              textTransform:
                "uppercase",
              fontWeight: 700,
              letterSpacing:
                ".05em",
            }}
          >
            Payment Percentage
          </div>

          <div
            style={{
              marginTop: 4,
              fontSize: 22,
              fontWeight: 800,
            }}
          >
            {percentage(
              selectedTermData?.payment_percentage
            )}
          </div>

        </div>


        <div
          style={{
            padding: 13,
            borderRadius: 10,
            background:
              "rgba(124,92,250,.06)",
          }}
        >

          <div
            style={{
              fontSize: 10,
              color:
                "var(--ink-dim)",
              textTransform:
                "uppercase",
              fontWeight: 700,
              letterSpacing:
                ".05em",
            }}
          >
            Technologies
          </div>

          <div
            style={{
              marginTop: 4,
              fontSize: 22,
              fontWeight: 800,
            }}
          >
            {technologies.length}
          </div>

        </div>

      </div>

    </div>
  );
}


/* =========================================================================
   TECHNOLOGY TABLE
   ========================================================================= */

function TechnologyAnalyticsTable({
  course,
  selectedTerm,
}) {
  const technologies =
    course?.technologies || [];


  if (!technologies.length) {
    return null;
  }


  return (
    <div className="panel">

      <h2>
        Technology-wise Analytics
      </h2>

      <div className="panel-sub">
        {selectedTerm} payment performance by technology
      </div>


      <div
        style={{
          overflowX: "auto",
          marginTop: 16,
        }}
      >

        <table
          style={{
            width: "100%",
            borderCollapse:
              "collapse",
            minWidth: 850,
          }}
        >

          <thead>

            <tr>

              {[
                "Technology",
                "Seats",
                "Registered",
                `${selectedTerm} Paid`,
                `${selectedTerm} Pending`,
                "Payment %",
                "Share %",
              ].map(
                (heading) => (
                  <th
                    key={heading}
                    style={{
                      padding:
                        "12px 10px",
                      textAlign:
                        heading ===
                          "Technology"
                          ? "left"
                          : "right",
                      fontSize: 10,
                      textTransform:
                        "uppercase",
                      letterSpacing:
                        ".04em",
                      color:
                        "#5b6472",
                      borderBottom:
                        "1px solid var(--border-soft)",
                      whiteSpace:
                        "nowrap",
                    }}
                  >
                    {heading}
                  </th>
                )
              )}

            </tr>

          </thead>


          <tbody>

            {technologies.map(
              (tech, idx) => {

                const term =
                  tech?.terms?.find(
                    (item) =>
                      String(
                        item?.term_name
                      ) ===
                      String(
                        selectedTerm
                      )
                  );


                const registered =
                  number(
                    tech?.registered_students
                  );


                const paid =
                  number(
                    term?.paid_students
                  );


                const pending =
                  number(
                    term?.pending_students
                  );


                const paymentPct =
                  number(
                    term?.payment_percentage
                  );


                const share =
                  number(
                    term?.share_percentage
                  );


                return (
                  <tr
                    key={
                      tech?.technology_id ||
                      idx
                    }
                  >

                    <td
                      style={{
                        padding:
                          "13px 10px",
                        borderBottom:
                          "1px solid var(--border-soft)",
                      }}
                    >

                      <div
                        style={{
                          display:
                            "flex",
                          alignItems:
                            "center",
                          gap: 9,
                        }}
                      >

                        <span
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius:
                              "50%",
                            background:
                              ACCENTS[
                                idx %
                                  ACCENTS.length
                              ],
                            flexShrink: 0,
                          }}
                        />

                        <span
                          style={{
                            fontWeight:
                              700,
                          }}
                        >
                          {cleanTechnologyName(
                            tech?.technology_name
                          )}
                        </span>

                      </div>

                    </td>


                    <td
                      style={{
                        padding:
                          "13px 10px",
                        textAlign:
                          "right",
                        borderBottom:
                          "1px solid var(--border-soft)",
                      }}
                    >
                      {number(
                        tech?.seats
                      )}
                    </td>


                    <td
                      style={{
                        padding:
                          "13px 10px",
                        textAlign:
                          "right",
                        fontWeight:
                          600,
                        borderBottom:
                          "1px solid var(--border-soft)",
                      }}
                    >
                      {registered}
                    </td>


                    <td
                      style={{
                        padding:
                          "13px 10px",
                        textAlign:
                          "right",
                        fontWeight:
                          800,
                        color:
                          "#0ea975",
                        borderBottom:
                          "1px solid var(--border-soft)",
                      }}
                    >
                      {paid}
                    </td>


                    <td
                      style={{
                        padding:
                          "13px 10px",
                        textAlign:
                          "right",
                        fontWeight:
                          600,
                        borderBottom:
                          "1px solid var(--border-soft)",
                      }}
                    >
                      {pending}
                    </td>


                    <td
                      style={{
                        padding:
                          "13px 10px",
                        textAlign:
                          "right",
                        fontWeight:
                          800,
                        borderBottom:
                          "1px solid var(--border-soft)",
                      }}
                    >
                      {percentage(
                        paymentPct
                      )}
                    </td>


                    <td
                      style={{
                        padding:
                          "13px 10px",
                        textAlign:
                          "right",
                        fontWeight:
                          700,
                        color:
                          "#7c5cfa",
                        borderBottom:
                          "1px solid var(--border-soft)",
                      }}
                    >
                      {percentage(
                        share
                      )}
                    </td>

                  </tr>
                );

              }
            )}


            {/* -------------------------------------------------------
                OVERALL ROW
                ------------------------------------------------------- */}

            <tr
              style={{
                background:
                  "rgba(59,109,246,.045)",
              }}
            >

              <td
                style={{
                  padding:
                    "14px 10px",
                  fontWeight:
                    800,
                }}
              >
                Overall
              </td>


              <td
                style={{
                  padding:
                    "14px 10px",
                  textAlign:
                    "right",
                  fontWeight:
                    800,
                }}
              >
                {technologies.reduce(
                  (sum, tech) =>
                    sum +
                    number(
                      tech?.seats
                    ),
                  0
                )}
              </td>


              <td
                style={{
                  padding:
                    "14px 10px",
                  textAlign:
                    "right",
                  fontWeight:
                    800,
                }}
              >
                {number(
                  course?.overall
                    ?.registered_students
                )}
              </td>


              <td
                style={{
                  padding:
                    "14px 10px",
                  textAlign:
                    "right",
                  fontWeight:
                    800,
                  color:
                    "#0ea975",
                }}
              >
                {number(
                  course
                    ?.overall_term_summary
                    ?.find(
                      (t) =>
                        String(
                          t?.term_name
                        ) ===
                        String(
                          selectedTerm
                        )
                    )
                    ?.paid_students
                )}
              </td>


              <td
                style={{
                  padding:
                    "14px 10px",
                  textAlign:
                    "right",
                  fontWeight:
                    800,
                }}
              >
                {number(
                  course
                    ?.overall_term_summary
                    ?.find(
                      (t) =>
                        String(
                          t?.term_name
                        ) ===
                        String(
                          selectedTerm
                        )
                    )
                    ?.pending_students
                )}
              </td>


              <td
                style={{
                  padding:
                    "14px 10px",
                  textAlign:
                    "right",
                  fontWeight:
                    800,
                }}
              >
                {percentage(
                  course
                    ?.overall_term_summary
                    ?.find(
                      (t) =>
                        String(
                          t?.term_name
                        ) ===
                        String(
                          selectedTerm
                        )
                    )
                    ?.payment_percentage
                )}
              </td>


              <td
                style={{
                  padding:
                    "14px 10px",
                  textAlign:
                    "right",
                    fontWeight:
                      800,
                }}
              >
                100%
              </td>

            </tr>

          </tbody>

        </table>

      </div>

    </div>
  );
}


/* =========================================================================
   SKELETON
   ========================================================================= */

function Skeleton() {
  return (
    <div className="grid">

      {Array.from({
        length: 6,
      }).map(
        (_, i) => (
          <div
            className="skeleton"
            key={i}
          />
        )
      )}

    </div>
  );
}


/* =========================================================================
   MAIN COMPONENT
   ========================================================================= */

export default function EnrollmentAnalytics() {

  const [
    course,
    setCourse,
  ] = useState(null);


  const [
    status,
    setStatus,
  ] = useState("loading");


  const [
    errorMsg,
    setErrorMsg,
  ] = useState("");


  const [
    lastUpdated,
    setLastUpdated,
  ] = useState("—");


  const [
    spinning,
    setSpinning,
  ] = useState(false);


  const [
    selectedCourseId,
    setSelectedCourseId,
  ] = useState(COURSE_IDS[0] || "");


  const [
    courseOptions,
    setCourseOptions,
  ] = useState([]);


  const [
    selectedTerm,
    setSelectedTerm,
  ] = useState("");


  /* =======================================================================
     LOAD DATA
     ======================================================================= */

  const loadData =
    useCallback(
      async () => {

        setSpinning(true);
        setStatus("loading");
        setErrorMsg("");


        try {

          if (!selectedCourseId) {
            throw new Error(
              "No course ID is configured"
            );
          }


          /*
           * The existing backend is used exactly as it is.
           * Only the selected course ID changes.
           */

          const url =
            `${API_URL}?course_ids=${encodeURIComponent(
              COURSE_IDS.join(",")
            )}`;


          const response =
            await fetch(
              url,
              {
                method: "POST",

                headers: {
                  Accept:
                    "application/json",
                  "Content-Type":
                    "application/json",
                },

                body: JSON.stringify({
                  course_ids: COURSE_IDS,
                }),
              }
            );


          if (!response.ok) {
            throw new Error(
              `HTTP ${response.status}`
            );
          }


          const json =
            await response.json();


          if (
            json?.success === false
          ) {
            throw new Error(
              json?.message ||
                "API returned an error"
            );
          }


          const allCourseData =
            Array.isArray(json?.data)
              ? json.data
              : [];


          const selectedCourseData =
            allCourseData.find(
              (item) =>
                String(item?.course_id) ===
                String(selectedCourseId)
            ) || null;


          setCourseOptions(allCourseData);


          const courseData = selectedCourseData;


          if (!courseData) {
            throw new Error(
              "No course analytics data found"
            );
          }


          setCourse(courseData);


          /*
           * Always default to the FIRST available term for the
           * currently selected course. This prevents a term from
           * the previous course being carried into the new course.
           */

          const backendTerms =
            Array.isArray(
              courseData?.selected_terms
            )
              ? courseData.selected_terms
              : Array.isArray(
                  courseData?.overall_term_summary
                )
              ? courseData.overall_term_summary.map(
                  (term) => term?.term_name
                )
              : [];


          setSelectedTerm(
            backendTerms[0] || ""
          );


          setLastUpdated(
            new Date().toLocaleTimeString()
          );


          setStatus("ready");

        } catch (error) {

          console.error(
            "Course Payment Analytics:",
            error
          );

          setCourse(null);
          setSelectedTerm("");

          setErrorMsg(
            error?.message ||
              "Unable to load analytics"
          );

          setStatus("error");

        } finally {

          setSpinning(false);

        }

      },
      [selectedCourseId]
    );


  /* =======================================================================
     INITIAL LOAD
     ======================================================================= */

  useEffect(
    () => {

      if (selectedCourseId) {
        loadData();
      }

    },
    [loadData, selectedCourseId]
  );


  /* =======================================================================
     DERIVED DATA
     ======================================================================= */

  const technologies =
    course?.technologies || [];


  const terms =
    course?.selected_terms ||
    course?.overall_term_summary?.map(
      (term) =>
        term.term_name
    ) ||
    [];


  const courseRegistered =
    number(
      course?.overall
        ?.registered_students
    ) ||
    number(
      course?.total_course_registered
    );


  const selectedTermData =
    course?.overall_term_summary?.find(
      (term) =>
        String(
          term?.term_name
        ) ===
        String(selectedTerm)
    );


  /* =======================================================================
     RENDER
     ======================================================================= */

  return (
    <div className="ea-root">

      <div className="aurora" />

      <div className="grain" />


      <div className="wrap">

        {/* ===============================================================
            HEADER
            =============================================================== */}

        <header>

          <div className="title-block">

            <p className="eyebrow">

              <span className="pulse-dot" />

              Live Course Payment Analytics

            </p>


            <h1>
              {course?.course_name ||
                "Course Analytics"}
            </h1>


            <div className="sub">

              Course-wise registrations,
              technology distribution and
              term-payment tracking

            </div>


            {status ===
              "ready" &&
              course && (

                <div className="course-badge">

                  Course ID

                  <b
                    style={{
                      marginLeft: 6,
                    }}
                  >
                    {course.course_id}
                  </b>

                  <span
                    style={{
                      marginLeft: 12,
                    }}
                  >
                    ·
                  </span>

                  <b
                    style={{
                      marginLeft: 8,
                    }}
                  >
                    {courseRegistered.toLocaleString()}
                  </b>

                  {" "}
                  total registered

                </div>

              )}

          </div>


          {/* -------------------------------------------------------------
              REFRESH
              ------------------------------------------------------------- */}

          <div className="refresh-row">

            <span>

              Updated{" "}

              <span id="lastUpdated">
                {lastUpdated}
              </span>

            </span>


            <button
              className={`btn ${
                spinning
                  ? "spinning"
                  : ""
              }`}
              onClick={loadData}
              disabled={spinning}
            >

              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
              >

                <path d="M21 12a9 9 0 1 1-3-6.7M21 3v6h-6" />

              </svg>

              {spinning
                ? "Refreshing..."
                : "Refresh"}

            </button>

          </div>

        </header>


        {/* ===============================================================
            COURSE SELECTOR
            =============================================================== */}

        <CourseSelector
          courseIds={COURSE_IDS}
          courseOptions={courseOptions}
          selectedCourseId={selectedCourseId}
          course={course}
          onChange={setSelectedCourseId}
          disabled={spinning}
        />


        {/* ===============================================================
            TERM SELECTOR
            =============================================================== */}

        {status ===
          "ready" &&
          terms.length > 0 && (

            <TermSelector
              terms={terms}
              selectedTerm={
                selectedTerm
              }
              onChange={
                setSelectedTerm
              }
            />

          )}


        {/* ===============================================================
            KPI
            =============================================================== */}

        {status ===
          "ready" &&
          course && (

            <KPIStrip
              course={course}
              selectedTerm={
                selectedTerm
              }
            />

          )}


        {/* ===============================================================
            CHARTS
            =============================================================== */}

        {status ===
          "ready" &&
          course && (

            <ChartsRow
              course={course}
              selectedTerm={
                selectedTerm
              }
            />

          )}


        {/* ===============================================================
            TERM-WISE COURSE SUMMARY
            =============================================================== */}

        {status ===
          "ready" &&
          course && (

            <div
              style={{
                marginTop: 22,
              }}
            >

              <TermSummaryTable
                course={course}
                selectedTerm={
                  selectedTerm
                }
              />

            </div>

          )}


        {/* ===============================================================
            TECHNOLOGY SECTION
            =============================================================== */}

        <div
          className="section-head"
          style={{
            marginTop: 26,
          }}
        >

          <h2>
            Technology Breakdown
          </h2>


          <span className="count-pill">

            {status ===
            "ready"
              ? `${technologies.length} ${
                  technologies.length ===
                  1
                    ? "technology"
                    : "technologies"
                }`
              : "— technologies"}

          </span>

        </div>


        {/* ===============================================================
            LOADING
            =============================================================== */}

        {status ===
          "loading" && (
            <Skeleton />
          )}


        {/* ===============================================================
            ERROR
            =============================================================== */}

        {status ===
          "error" && (

            <div className="state-box error">

              Couldn't load course
              analytics —

              {" "}

              {errorMsg}

              <br />
              <br />

              <span
                style={{
                  color:
                    "var(--ink-dim)",
                }}
              >
                Check the course ID,
                API URL and backend
                availability.
              </span>

            </div>

          )}


        {/* ===============================================================
            NO DATA
            =============================================================== */}

        {status ===
          "ready" &&
          technologies.length ===
            0 && (

            <div className="state-box">

              No technologies found
              for this course.

            </div>

          )}


        {/* ===============================================================
            TECHNOLOGY CARDS
            =============================================================== */}

        {status ===
          "ready" &&
          technologies.length >
            0 && (

            <div className="grid">

              {technologies.map(
                (
                  tech,
                  index
                ) => (

                  <TechCard
                    key={
                      tech?.technology_id ||
                      index
                    }
                    tech={tech}
                    idx={index}
                    selectedTerm={
                      selectedTerm
                    }
                  />

                )
              )}

            </div>

          )}


        {/* ===============================================================
            TECHNOLOGY TABLE
            =============================================================== */}

        {status ===
          "ready" &&
          technologies.length >
            0 && (

            <div
              style={{
                marginTop: 24,
              }}
            >

              <TechnologyAnalyticsTable
                course={course}
                selectedTerm={
                  selectedTerm
                }
              />

            </div>

          )}


        {/* ===============================================================
            FOOTER
            =============================================================== */}

        <footer>

          course-payment-analytics ·{" "}
          {course?.course_name ||
            "Course"}

          {" · "}

          Course ID:{" "}

          {course?.course_id ||
            selectedCourseId}

        </footer>

      </div>

    </div>
  );
}